import type { MapEntry } from '../maps/catalog';
import { mb } from '../maps/useCatalog';
import { ColorText } from './ColorText';
import { Icon } from './Icon';
import { Levelshot } from './Levelshot';

export interface MapCardProps {
  entry: MapEntry;
  selected?: boolean;
  onSelect: () => void;
  onToggleFavorite?: () => void;
}

export function MapCard({ entry, selected, onSelect, onToggleFavorite }: MapCardProps) {
  const runs = entry.meta?.runs.length ?? 0;
  const players = entry.info?.players ?? 0;
  return (
    <article class={`map-card${selected ? ' selected' : ''}`}>
      <button type="button" class="map-card-main" onClick={onSelect} aria-label={`${entry.title} (${entry.name})`}>
        <Levelshot map={entry.name} class="map-card-shot" />
        <div class="map-card-badges">
          {runs > 0 && (
            <span class="badge">
              <Icon name="flag" size={12} />
              {runs} {runs === 1 ? 'run' : 'runs'}
            </span>
          )}
          {players > 0 && (
            <span class="badge badge-good">
              <Icon name="users" size={12} />
              {players}
            </span>
          )}
          {entry.info?.difficulty && (
            <span class="badge" title="Difficulty (1 easy .. 5 hard)">
              {'●'.repeat(entry.info.difficulty)}
            </span>
          )}
          {!entry.local && entry.info && (
            <span class="badge" title="Downloaded on first play">
              <Icon name="download" size={12} />
              {mb(entry.info.bundleSize)}
            </span>
          )}
        </div>
        <div class="map-card-body">
          <div class="map-card-title">{entry.longname ? <ColorText text={entry.longname} /> : entry.name}</div>
          <div class="map-card-meta">
            <span class="map-card-name">{entry.name}</span>
          </div>
        </div>
      </button>
      {onToggleFavorite && (
        <button
          type="button"
          class={`map-card-fav${entry.favorite ? ' on' : ''}`}
          aria-pressed={entry.favorite}
          aria-label={entry.favorite ? `Remove ${entry.name} from favorites` : `Add ${entry.name} to favorites`}
          title={entry.favorite ? 'Favorite' : 'Add to favorites'}
          onClick={onToggleFavorite}
        >
          <Icon name="star" size={16} filled={entry.favorite} />
        </button>
      )}
    </article>
  );
}
